import { AppUser } from '@eventr/common'
import {
    ColoursObj,
    Typography,
    drawerStore,
    getContrastYIQ,
} from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import { ArrowFlip } from '../../assets/animated/arrowFlip'
import { AccordionTransition } from '../../common/AccordionTransition'
import { bs, useBoundStore } from '../../state/bound.store'
import { DirectChatButton } from '../components/directChatButton'
import { TeamMember } from '../overlay/TeamMember'
import { MemberProfilePic } from './MemberProfilePic'

interface ITeamDepartmentsProps {
    tag: string
    users: AppUser[]
    currentUser: AppUser | undefined
    hideHeader?: boolean
}

export const TeamDepartments = ({
    users,
    tag,
    currentUser,
    hideHeader,
}: ITeamDepartmentsProps) => {
    const [open, setOpen] = useState<boolean>(false)
    const localUsers = users.filter((userFilter) => {
        return tag !== 'Other'
            ? userFilter.departmentTags?.includes(tag) ||
                  userFilter.groupTags?.includes(tag)
            : true
    })

    useEffect(() => {
        if (hideHeader) {
            setOpen(true)
        }
    }, [hideHeader])
    if (localUsers.length < 1) {
        return null
    }

    return (
        <Stack margin={'0 0 8px 0'}>
            {!hideHeader && (
                <div
                    onClick={(event) => {
                        setOpen(!open)
                        event.stopPropagation()
                    }}
                    style={{
                        padding: '0px 8px',
                        backgroundColor: 'transparent',
                    }}
                >
                    <Stack
                        direction="row"
                        padding="12px"
                        borderRadius="8px"
                        bgcolor={ColoursObj.backgrounds.secondary}
                        justifyContent="space-between"
                    >
                        <Stack direction="row" gap="8px">
                            <ArrowFlip
                                open={!open}
                                colour={ColoursObj.font.primary}
                            />

                            <Typography
                                variant="bodyBold"
                                color={ColoursObj.font.primary}
                            >
                                {tag}
                            </Typography>
                        </Stack>

                        <Typography
                            variant="bodySubtext"
                            color={getContrastYIQ('#EDF3F4')}
                            padding="4px 8px"
                            bgcolor="#EDF3F4"
                            borderRadius="4px"
                        >
                            {localUsers.length}
                        </Typography>
                    </Stack>
                </div>
            )}

            <AccordionTransition open={open}>
                <Stack gap="4px" maxWidth="100vw" padding={'8px 0px'}>
                    {open &&
                        localUsers.map((user, index) => {
                            return (
                                <UserRow
                                    key={index}
                                    currentUser={currentUser ?? ({} as AppUser)}
                                    user={user}
                                />
                            )
                        })}
                </Stack>
            </AccordionTransition>
        </Stack>
    )
}

interface UserRowProps {
    currentUser: AppUser
    user: AppUser
}

const UserRow = ({ currentUser, user }: UserRowProps) => {
    const setOverride = useBoundStore((s) => s.setOverride)

    const [isHeld, setIsHeld] = useState<boolean>(false)

    function label(): string {
        return bs().getNameFormatted(user)
    }

    function action() {
        if (user.firstName && user.lastName && user.id !== currentUser?.id) {
            setOverride(user.id, false)
            drawerStore().openDrawer({ node: <TeamMember /> })
        }
    }

    const content = (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            paddingY="4px"
            paddingX="8px"
            overflow={'hidden'}
        >
            <div
                onMouseEnter={() => setIsHeld(true)}
                onMouseLeave={() => setIsHeld(false)}
                onMouseDown={() => setIsHeld(true)}
                onMouseUp={() => setIsHeld(false)}
                onTouchStart={() => setIsHeld(true)}
                onTouchEnd={() => {
                    setIsHeld(false)
                }}
                onTouchMove={() => setIsHeld(false)}
                onClick={(event) => {
                    event.stopPropagation()

                    action()
                }}
                style={{ width: '100%', maxWidth: 'calc(100% - 78px)' }}
            >
                <Stack
                    direction="row"
                    gap="16px"
                    alignItems="center"
                    overflow="hidden"
                    width="100%"
                >
                    <MemberProfilePic user={user} />

                    <Typography
                        variant="bodyRegular"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        width="100%"
                        color={
                            !(user.firstName && user.lastName)
                                ? ColoursObj.shades.shade1
                                : ColoursObj.core.primary
                        }
                    >
                        {label()}
                    </Typography>
                </Stack>
            </div>

            {user?.id !== currentUser?.id ? (
                user?.firstName && user?.lastName ? (
                    <DirectChatButton
                        user_id={currentUser?.id ?? -1}
                        user_id2={user?.id}
                        fullWidth={false}
                    />
                ) : (
                    <Typography
                        variant="bodyRegular"
                        color={getContrastYIQ(ColoursObj.backgrounds.primary)}
                        paddingLeft="16px"
                    >
                        Pending
                    </Typography>
                )
            ) : (
                <Typography
                    variant="bodyRegular"
                    color={getContrastYIQ(ColoursObj.backgrounds.primary)}
                    paddingLeft="16mempx"
                >
                    You
                </Typography>
            )}
        </Stack>
    )

    return user &&
        user.firstName &&
        user.lastName &&
        user.id !== currentUser?.id ? (
        <div
            style={{
                backgroundColor: ColoursObj.backgrounds.primary,
                filter: isHeld ? 'brightness(97.5%)' : undefined,
            }}
        >
            {content}
        </div>
    ) : (
        <div>{content}</div>
    )
}
